/**
 * Identyfikatory pomiaru (GA4, Google Ads, Meta) zapisywane w rezerwacji przy
 * „Przejdź do płatności” – backend wysyła z nich `purchase`, gdy klient nie
 * wraca na podsumowanie (wytyczne pomiaru, zadania 1a i 7b).
 *
 * Wszystkie funkcje są odporne na brak / zły format danych: zwracają `null`,
 * nigdy nie rzucają – pomiar nie może zablokować rezerwacji.
 */

export type Consent = "granted" | "denied";

export interface CheckoutTracking {
  gaClientId: string | null;
  gaSessionId: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  analyticsConsent: Consent | null;
  adsConsent: Consent | null;
  fbp: string | null;
  fbc: string | null;
}

/** Atrybucja z URL wejścia do `/creator` (sessionStorage). */
export interface EntryAttribution {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  /** Czas wejścia z `fbclid` (ms) – do zbudowania `fbc`. */
  fbclidAt?: number;
}

export const ATTRIBUTION_STORAGE_KEY = "p24Attribution";
/** Strumień GA4 `G-KBYV8KS7G4` → ciasteczko sesji `_ga_KBYV8KS7G4`. */
export const GA4_SESSION_COOKIE = "_ga_KBYV8KS7G4";

const CLICK_ID = /^[A-Za-z0-9_\-.~]{1,500}$/;

/** Wartość ciasteczka z `document.cookie` (pierwsze wystąpienie) albo `null`. */
export function readCookie(cookieString: string | null | undefined, name: string): string | null {
  try {
    for (const part of String(cookieString ?? "").split(";")) {
      const index = part.indexOf("=");
      if (index < 0) continue;
      if (part.slice(0, index).trim() === name) {
        const value = part.slice(index + 1).trim();
        try {
          return decodeURIComponent(value) || null;
        } catch {
          return value || null;
        }
      }
    }
  } catch {
    /* uszkodzony cookie string – traktujemy jak brak */
  }
  return null;
}

/** `_ga` = `GA1.1.123456789.1700000000` → `123456789.1700000000`. */
export function parseGaClientId(value: string | null | undefined): string | null {
  const parts = String(value ?? "").split(".");
  if (parts.length < 4) return null;
  const clientId = parts.slice(-2).join(".");
  return /^\d{1,20}\.\d{1,20}$/.test(clientId) ? clientId : null;
}

/**
 * `_ga_<ID>`: `GS1.1.<session_id>.…` → 3. segment;
 * `GS2.1.s<session_id>$o…` → liczba po `s` do pierwszego `$`.
 */
export function parseGaSessionId(value: string | null | undefined): string | null {
  const text = String(value ?? "");
  const gs2 = /^GS2\.\d+\.s(\d{1,20})(?:\$|$)/.exec(text);
  if (gs2) return gs2[1];
  const parts = text.split(".");
  if (parts[0] === "GS1" && /^\d{1,20}$/.test(parts[2] ?? "")) return parts[2];
  return null;
}

/** `_gcl_aw` = `GCL.<ts>.<gclid>` → gclid (ostatni segment). */
export function parseGclAw(value: string | null | undefined): string | null {
  const parts = String(value ?? "").split(".");
  if (parts.length < 3 || parts[0] !== "GCL") return null;
  const gclid = parts.slice(2).join(".");
  return CLICK_ID.test(gclid) ? gclid : null;
}

/**
 * `_fbp`: fb.<subdomena>.<czas ms>.<losowa>[.<dopisek>] – piksel Meta (z
 * `meta-capi-param-builder-clientjs`) dopisuje piąty człon, np.
 * `fb.1.1791805393427.928875271180214542.AQYAAQMC`. Wartość idzie do Meta bez zmian.
 */
const FBP = /^fb\.\d\.\d{10,16}\.\d{1,20}(\.[A-Za-z0-9_\-]{1,16})?$/;
/** `_fbc`: fb.<subdomena>.<czas ms>.<fbclid>[.<dopisek>]. */
const FBC = /^fb\.\d\.\d{10,16}\.[A-Za-z0-9_\-.~]{1,480}$/;

/**
 * Atrybucja z parametrów URL wejścia; zwraca `null`, gdy w URL nie ma żadnego
 * identyfikatora kliknięcia (wtedy nie nadpisujemy zapamiętanej atrybucji).
 */
export function attributionFromQuery(
  params: { get(name: string): string | null },
  now: number,
): EntryAttribution | null {
  const pick = (name: string) => {
    const value = params.get(name)?.trim();
    return value && CLICK_ID.test(value) ? value : undefined;
  };
  const attribution: EntryAttribution = {
    gclid: pick("gclid"),
    gbraid: pick("gbraid"),
    wbraid: pick("wbraid"),
    fbclid: pick("fbclid"),
  };
  if (attribution.fbclid) attribution.fbclidAt = now;
  const present = Object.fromEntries(
    Object.entries(attribution).filter(([, value]) => value !== undefined),
  ) as EntryAttribution;
  return Object.keys(present).length ? present : null;
}

/**
 * Stan zgód CookieScript: `performance` → analytics, `targeting` → ads.
 * `currentState()` zwraca `{ action, categories: string[] }`; bez CMP → `null`.
 */
export function consentFromCookieScript(state: unknown): {
  analyticsConsent: Consent | null;
  adsConsent: Consent | null;
} {
  const categories = (state as { categories?: unknown } | null)?.categories;
  if (!Array.isArray(categories)) {
    return { analyticsConsent: null, adsConsent: null };
  }
  const has = (name: string): Consent =>
    categories.includes(name) ? "granted" : "denied";
  return { analyticsConsent: has("performance"), adsConsent: has("targeting") };
}

export interface TrackingSources {
  cookies: string;
  attribution: EntryAttribution | null;
  cookieScriptState: unknown;
}

/** Zbiera identyfikatory pomiaru do zapisu w rezerwacji. */
export function collectCheckoutTracking(sources: TrackingSources): CheckoutTracking {
  const { cookies, attribution } = sources;
  const fbp = readCookie(cookies, "_fbp");
  let fbc = readCookie(cookies, "_fbc");
  if (!fbc && attribution?.fbclid && attribution.fbclidAt) {
    fbc = `fb.1.${attribution.fbclidAt}.${attribution.fbclid}`;
  }
  return {
    gaClientId: parseGaClientId(readCookie(cookies, "_ga")),
    gaSessionId: parseGaSessionId(readCookie(cookies, GA4_SESSION_COOKIE)),
    gclid: attribution?.gclid ?? parseGclAw(readCookie(cookies, "_gcl_aw")),
    gbraid: attribution?.gbraid ?? null,
    wbraid: attribution?.wbraid ?? null,
    ...consentFromCookieScript(sources.cookieScriptState),
    fbp: fbp && FBP.test(fbp) ? fbp : null,
    fbc: fbc && FBC.test(fbc) ? fbc : null,
  };
}
